import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/Api';
import AuthContext from '../context/AuthContext';

const Coursework = () => {
  const [courseworks, setCourseworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { regNumber } = useContext(AuthContext);
  const [selectedComment, setSelectedComment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchCourseworks = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/courseworks/student-results/${regNumber}`);
        setCourseworks(response.data.results);
        setError(null);
      } catch (err) {
        console.error('Error fetching courseworks:', err);
        setError('Failed to fetch courseworks');
      } finally {
        setLoading(false);
      }
    };

    if (regNumber) {
      fetchCourseworks();
    }
  }, [regNumber]);

  const handleCommentClick = (coursework) => {
    setSelectedComment({
      subject: coursework.Subject,
      description: coursework.Description,
      comment: coursework.Comments
    });
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Courseworks Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">My Courseworks</h2>
        </div>
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#11024D]">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Subject
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Class
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Teacher
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Comments
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Score
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {courseworks.map((coursework) => (
                  <tr key={coursework.ResultID} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {coursework.Subject}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="line-clamp-2">
                        {coursework.Description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{coursework.ClassName}</div>
                      <div className="text-xs text-gray-500">{coursework.Form}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {coursework.TeacherName} {coursework.TeacherSurname}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {coursework.Comments ? (
                        <button
                          onClick={() => handleCommentClick(coursework)}
                          className="text-sm text-gray-900 hover:text-purple-600 text-left"
                        >
                          <div className="line-clamp-2">
                            {coursework.Comments}
                          </div>
                        </button>
                      ) : (
                        <span className="text-sm text-gray-500">No comments</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-sm rounded-full ${
                        coursework.Score 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {coursework.Score 
                          ? `${coursework.Score}/${coursework.TotalMarks}`
                          : 'Not Marked'
                        }
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Comment Modal */}
      {isModalOpen && selectedComment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedComment.subject}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {selectedComment.description}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-800 whitespace-pre-wrap">
                {selectedComment.comment}
              </p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Coursework;
